import React, { useState, useEffect } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import {
  getJobs, getScreeningTemplate, saveScreeningTemplate, listScreeningTemplates,
  generateScreeningQuestions,
  listScreeningSessions, getScreeningSession,
  createScreeningSession, deleteScreeningSession, rescoreScreeningSession,
  resendScreeningSession, getScreeningVideoUrl, exportScreeningSessions,
  getScreeningDefaults,
} from "../api";
import { useToast } from "../context/ToastContext";
import { formatDate } from "../utils/dateFormat";
import { useTenantTimezone } from "../hooks/useTenantTimezone";
import { LoadingOverlay } from "../components/LoadingOverlay";
import { AsyncButton } from "../components/AsyncButton";

const REC_COLORS: Record<string, { bg: string; color: string; label: string }> = {
  strong_yes: { bg: "#dcfce7", color: "#166534", label: "Strong Yes" },
  yes:        { bg: "#dbeafe", color: "#1d4ed8", label: "Yes" },
  maybe:      { bg: "#fef9c3", color: "#854d0e", label: "Maybe" },
  no:         { bg: "#fee2e2", color: "#991b1b", label: "No" },
};

const STATUS_COLORS: Record<string, { bg: string; color: string }> = {
  pending:    { bg: "#f1f5f9", color: "#64748b" },
  in_progress:{ bg: "#dbeafe", color: "#1d4ed8" },
  completed:  { bg: "#fef9c3", color: "#854d0e" },
  processing: { bg: "#ede9fe", color: "#6d28d9" },
  scored:     { bg: "#dcfce7", color: "#166534" },
  expired:    { bg: "#fee2e2", color: "#991b1b" },
  error:      { bg: "#fee2e2", color: "#991b1b" },
};

const FLAG_LABELS: Record<string, string> = {
  tab_switches:    "Left the browser tab during recording",
  fullscreen_exits: "Exited fullscreen mode during recording",
  resumed:         "Session was interrupted and reopened",
  short_answers:   "Multiple very short answers (< 20s)",
  rerecorded:      "Re-recorded answers multiple times (possible rehearsal)",
  no_face:         "No face detected on camera for 10+ seconds during recording",
};

function flagTooltip(flags: string[]): string {
  return flags.map(f => {
    const [key, val] = f.split(":");
    const label = FLAG_LABELS[key] || key;
    return val ? `${label} (${val}×)` : label;
  }).join("\n");
}

const card: React.CSSProperties = { background: "#fff", border: "1px solid #e2e8f0", borderRadius: 10, padding: 20, marginBottom: 16 };
const labelStyle: React.CSSProperties = { fontSize: 12, fontWeight: 600, color: "#64748b", textTransform: "uppercase", letterSpacing: "0.05em", display: "block", marginBottom: 4 };
const inputStyle: React.CSSProperties = { width: "100%", border: "1px solid #e2e8f0", borderRadius: 6, padding: "8px 12px", fontSize: 14, boxSizing: "border-box" };
const btnStyle: React.CSSProperties = { background: "#0f766e", color: "#fff", border: "none", borderRadius: 6, padding: "9px 18px", cursor: "pointer", fontWeight: 600, fontSize: 14 };
const outlineBtn: React.CSSProperties = { background: "#fff", color: "#0f766e", border: "1px solid #0f766e", borderRadius: 6, padding: "8px 16px", cursor: "pointer", fontWeight: 600, fontSize: 14 };
const dangerBtn: React.CSSProperties = { background: "#fff", color: "#dc2626", border: "1px solid #fca5a5", borderRadius: 6, padding: "8px 14px", cursor: "pointer", fontSize: 13 };

export default function ScreeningPage() {
  const tz = useTenantTimezone();
  const [tab, setTab] = useState<"sessions" | "setup" | "rankings">("sessions");
  const [selectedJobId, setSelectedJobId] = useState<number | null>(null);
  const [sessionDetail, setSessionDetail] = useState<any>(null);
  const [showInviteModal, setShowInviteModal] = useState(false);
  const [inviteJobId, setInviteJobId] = useState<number | null>(null);
  const [inviteEmail, setInviteEmail] = useState("");
  const [inviteName, setInviteName] = useState("");
  const [playingAnswer, setPlayingAnswer] = useState<{ sessionId: number; answerId: number } | null>(null);
  const { toast } = useToast();
  const qc = useQueryClient();

  const [winW, setWinW] = useState(window.innerWidth);
  useEffect(() => {
    const h = () => setWinW(window.innerWidth);
    window.addEventListener("resize", h);
    return () => window.removeEventListener("resize", h);
  }, []);
  const isMobile = winW < 640;

  const { data: jobs = [] } = useQuery({ queryKey: ["jobs"], queryFn: getJobs });
  const { data: configuredTemplates = [] } = useQuery({ queryKey: ["screening-templates"], queryFn: listScreeningTemplates });
  const { data: screeningDefaults } = useQuery({ queryKey: ["screening-defaults"], queryFn: getScreeningDefaults });
  const { data: sessions = [], isLoading: loadingSessions } = useQuery({
    queryKey: ["screening-sessions"],
    queryFn: () => listScreeningSessions(),
  });
  const { data: template, isLoading: loadingTemplate } = useQuery({
    queryKey: ["screening-template", selectedJobId],
    queryFn: () => getScreeningTemplate(selectedJobId!),
    enabled: !!selectedJobId && tab === "setup",
  });
  const { data: detail } = useQuery({
    queryKey: ["screening-session", sessionDetail?.id],
    queryFn: () => getScreeningSession(sessionDetail.id),
    enabled: !!sessionDetail,
    refetchInterval: (query: any) => {
      const status = query.state?.data?.status ?? sessionDetail?.status;
      return status === "processing" ? 5000 : false;
    },
  });

  const [recFilter, setRecFilter] = useState<string>("all");
  const [sessionSearch, setSessionSearch] = useState("");

  // Template editor state
  const [tmplForm, setTmplForm] = useState<any>(null);
  React.useEffect(() => {
    if (template) {
      setTmplForm({
        enabled: template.enabled ?? true,
        intro_text: template.intro_text || "",
        time_limit_seconds: template.time_limit_seconds || 120,
        notify_hr: template.notify_hr ?? false,
        practice_question: template.practice_question || "",
        questions: template.questions?.length
          ? template.questions.map((q: any) => ({ ...q }))
          : [{ question_text: "", rubric: "", time_limit_seconds: null }],
      });
    }
  }, [template]);

  const saveTmplMut = useMutation({
    mutationFn: (data: any) => saveScreeningTemplate(selectedJobId!, data),
    onSuccess: () => {
      toast("Template saved", "success");
      qc.invalidateQueries({ queryKey: ["screening-template", selectedJobId] });
      qc.invalidateQueries({ queryKey: ["screening-templates"] });
    },
    onError: () => toast("Failed to save template", "error"),
  });

  const generateMut = useMutation({
    mutationFn: () => generateScreeningQuestions(selectedJobId!),
    onSuccess: (res: any) => {
      if (res.questions?.length) {
        setTmplForm((f: any) => ({
          ...f,
          questions: res.questions,
          ...(res.intro_text ? { intro_text: res.intro_text } : {}),
          ...(res.practice_question ? { practice_question: res.practice_question } : {}),
        }));
        toast(`Generated ${res.questions.length} questions + intro & practice text`, "success");
      }
    },
    onError: () => toast("AI generation failed — check the job has a description", "error"),
  });

  const inviteMut = useMutation({
    mutationFn: (data: any) => createScreeningSession(data),
    onSuccess: () => {
      toast("Invite sent!", "success");
      setShowInviteModal(false); setInviteEmail(""); setInviteName(""); setInviteJobId(null);
      qc.invalidateQueries({ queryKey: ["screening-sessions"] });
    },
    onError: (e: any) => toast(e?.response?.data?.detail || "Failed to send invite", "error"),
  });

  const deleteMut = useMutation({
    mutationFn: deleteScreeningSession,
    onSuccess: () => { toast("Session deleted", "success"); qc.invalidateQueries({ queryKey: ["screening-sessions"] }); },
    onError: () => toast("Failed to delete", "error"),
  });

  const rescoreMut = useMutation({
    mutationFn: rescoreScreeningSession,
    onSuccess: () => { toast("Re-scoring started — refresh in a minute", "success"); qc.invalidateQueries({ queryKey: ["screening-sessions"] }); },
    onError: () => toast("Failed to trigger re-score", "error"),
  });

  const resendMut = useMutation({
    mutationFn: resendScreeningSession,
    onSuccess: () => { toast("Invite resent!", "success"); qc.invalidateQueries({ queryKey: ["screening-sessions"] }); },
    onError: (e: any) => toast(e?.response?.data?.detail || "Failed to resend", "error"),
  });

  const activeJobs = Array.isArray(jobs) ? jobs.filter((j: any) => j.status === "open" || j.status === "active") : [];

  function addQuestion() {
    setTmplForm((f: any) => ({ ...f, questions: [...f.questions, { question_text: "", rubric: "", time_limit_seconds: null }] }));
  }
  function removeQuestion(i: number) {
    setTmplForm((f: any) => ({ ...f, questions: f.questions.filter((_: any, idx: number) => idx !== i) }));
  }
  function updateQuestion(i: number, field: string, value: any) {
    setTmplForm((f: any) => {
      const qs = [...f.questions];
      qs[i] = { ...qs[i], [field]: value };
      return { ...f, questions: qs };
    });
  }

  const scoreBar = (score: number) => {
    const pct = (score / 5) * 100;
    const color = score >= 4 ? "#16a34a" : score >= 3 ? "#d97706" : score > 0 ? "#dc2626" : "#94a3b8";
    return (
      <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
        <div style={{ flex: 1, height: 6, background: "#e2e8f0", borderRadius: 3 }}>
          <div style={{ width: `${pct}%`, height: "100%", background: color, borderRadius: 3 }} />
        </div>
        <span style={{ fontSize: 13, fontWeight: 700, color, minWidth: 28 }}>{score.toFixed(1)}</span>
      </div>
    );
  };

  // ── Session detail panel ─────────────────────────────────────────────────
  if (sessionDetail) {
    const s = detail || sessionDetail;
    const rec = s.recommendation ? REC_COLORS[s.recommendation] : null;
    return (
      <div style={{ maxWidth: 860, margin: "0 auto", padding: isMobile ? "16px 12px" : "24px 16px" }}>
        <button onClick={() => setSessionDetail(null)} style={{ ...outlineBtn, marginBottom: 20 }}>← Back</button>
        <div style={card}>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", flexWrap: "wrap", gap: 12 }}>
            <div style={{ minWidth: 0, flex: 1 }}>
              <div style={{ fontWeight: 700, fontSize: isMobile ? 16 : 18, wordBreak: "break-word" }}>{s.candidate_name || s.candidate_email}</div>
              <div style={{ color: "#64748b", fontSize: 13, wordBreak: "break-all" }}>{s.candidate_email} · {s.job_title}</div>
            </div>
            <div style={{ display: "flex", gap: 10, alignItems: "center", flexShrink: 0 }}>
              {rec && <span style={{ background: rec.bg, color: rec.color, borderRadius: 6, padding: "4px 12px", fontWeight: 700, fontSize: 14 }}>{rec.label}</span>}
              {s.overall_score != null && (
                <span style={{ fontWeight: 700, fontSize: 22, color: "#0f766e" }}>{s.overall_score.toFixed(1)}<span style={{ fontSize: 14, color: "#94a3b8" }}>/5</span></span>
              )}
            </div>
          </div>
          {s.ai_summary && (
            <div style={{ marginTop: 16, padding: 14, background: "#f0fdf4", borderRadius: 8, borderLeft: "3px solid #16a34a", fontSize: 14, color: "#166534", lineHeight: 1.6 }}>
              {s.ai_summary}
            </div>
          )}
          {s.integrity_flags && s.integrity_flags.length > 0 && (
            <div style={{ marginTop: 14, padding: 12, background: "#fef3c7", borderRadius: 8, borderLeft: "3px solid #d97706" }}>
              <div style={{ fontWeight: 700, fontSize: 13, color: "#92400e", marginBottom: 6 }}>⚠ Integrity Flags</div>
              <div style={{ display: "flex", gap: 6, flexWrap: "wrap", marginBottom: 8 }}>
                {s.integrity_flags.map((flag: string) => {
                  const [key] = flag.split(":");
                  const label = FLAG_LABELS[key] || flag;
                  return (
                    <span key={flag} title={label}
                      style={{ background: "#fff", border: "1px solid #fde68a", borderRadius: 5, padding: "2px 8px", fontSize: 12, color: "#92400e", fontWeight: 600, cursor: "help" }}>
                      {flag}
                    </span>
                  );
                })}
              </div>
              <details style={{ fontSize: 11, color: "#78350f" }}>
                <summary style={{ cursor: "pointer", fontWeight: 600 }}>What do these flags mean?</summary>
                <ul style={{ margin: "6px 0 0 16px", lineHeight: 1.7 }}>
                  <li><strong>tab_switches:N</strong> — candidate left the browser tab N times while recording. ≥3 is flagged.</li>
                  <li><strong>fullscreen_exits:N</strong> — exited fullscreen N times. ≥2 is flagged (may indicate looking at notes).</li>
                  <li><strong>resumed:N</strong> — session was closed and reopened N times. ≥2 may mean session sharing.</li>
                  <li><strong>short_answers:N</strong> — N answers were under 20 seconds, suggesting low engagement.</li>
                  <li><strong>rerecorded:N</strong> — answer was discarded and re-recorded N times total. ≥3 may indicate rehearsal after seeing the question.</li>
                  <li><strong>no_face:N</strong> — no face was detected on camera for 10+ continuous seconds, N time(s) total. ≥2 is flagged (may indicate someone else answering, or candidate stepping away).</li>
                </ul>
                <p style={{ margin: "6px 0 0", color: "#92400e" }}>Flags are signals, not proof. Review the video and use your judgement.</p>
              </details>
            </div>
          )}
          {s.status === "processing" && (
            <div style={{ marginTop: 12, padding: 12, background: "#ede9fe", borderRadius: 8, fontSize: 14, color: "#6d28d9" }}>
              ⏳ AI is processing the responses — this takes 1-2 minutes. This page will refresh automatically.
            </div>
          )}
          {(s.status === "error" || s.status === "scored" || s.status === "completed") && (
            <div style={{ marginTop: 12, display: "flex", justifyContent: "flex-end" }}>
              <button onClick={() => rescoreMut.mutate(s.id)} disabled={rescoreMut.variables === s.id && rescoreMut.isPending}
                style={{ background: "#ede9fe", color: "#6d28d9", border: "none", padding: "7px 18px", borderRadius: 7, cursor: "pointer", fontWeight: 600, fontSize: 13 }}>
                {rescoreMut.variables === s.id && rescoreMut.isPending ? "Rescoring…" : "Re-score with AI"}
              </button>
            </div>
          )}
        </div>

        {(detail?.answers || []).sort((a: any, b: any) => a.order_index - b.order_index).map((answer: any) => (
          <div key={answer.id} style={card}>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: 6, gap: 8 }}>
              <div style={{ fontWeight: 600, fontSize: 14, flex: 1 }}>Q{answer.order_index + 1}: {answer.question_text}</div>
              <div style={{ display: "flex", gap: 4, flexWrap: "wrap", flexShrink: 0 }}>
                {answer.tab_switches > 0 && (
                  <span title={`${answer.tab_switches} tab switch${answer.tab_switches > 1 ? "es" : ""} detected`}
                    style={{ background: "#fef3c7", color: "#92400e", borderRadius: 5, padding: "2px 8px", fontSize: 12, fontWeight: 700, whiteSpace: "nowrap" }}>
                    ⚠️ {answer.tab_switches}x tab switch
                  </span>
                )}
                {answer.no_face_events > 0 && (
                  <span title={`No face detected on camera ${answer.no_face_events} time${answer.no_face_events > 1 ? "s" : ""} (10+ continuous seconds)`}
                    style={{ background: "#fef3c7", color: "#92400e", borderRadius: 5, padding: "2px 8px", fontSize: 12, fontWeight: 700, whiteSpace: "nowrap" }}>
                    ⚠️ {answer.no_face_events}x no face
                  </span>
                )}
              </div>
            </div>
            {answer.rubric && <div style={{ fontSize: 12, color: "#64748b", marginBottom: 10 }}>Rubric: {answer.rubric}</div>}
            <div style={{ display: "flex", gap: 16, flexWrap: "wrap", alignItems: "flex-start" }}>
              <div style={{ flex: 1, minWidth: isMobile ? "100%" : 280 }}>
                {answer.score != null && (
                  <div style={{ marginBottom: 10 }}>
                    {answer.score === 0 && answer.score_reasoning?.startsWith("No answer detected") ? (
                      <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 6 }}>
                        <span style={{ background: "#fee2e2", color: "#991b1b", borderRadius: 5, padding: "3px 10px", fontSize: 12, fontWeight: 700 }}>⚠️ No answer detected</span>
                      </div>
                    ) : scoreBar(answer.score)}
                    {answer.score_reasoning && (
                      <div style={{ fontSize: 13, color: answer.score === 0 && answer.score_reasoning?.startsWith("No answer detected") ? "#991b1b" : "#475569", marginTop: 4 }}>
                        {answer.score_reasoning.replace(/^No answer detected:\s*/i, "")}
                      </div>
                    )}
                  </div>
                )}
                {answer.transcript ? (
                  <div style={{ fontSize: 13, color: "#334155", lineHeight: 1.7, background: "#f8fafc", padding: 12, borderRadius: 6 }}>
                    <div style={{ fontSize: 11, fontWeight: 700, color: "#94a3b8", marginBottom: 4 }}>TRANSCRIPT</div>
                    {answer.transcript}
                  </div>
                ) : (
                  <div style={{ fontSize: 13, color: "#94a3b8" }}>
                    {!answer.has_video
                      ? "No response"
                      : (s.status === "scored" || s.status === "error")
                        ? "Scoring unavailable for this answer"
                        : "Processing…"}
                  </div>
                )}
              </div>
              {answer.has_video && (
                <div style={{ width: isMobile ? "100%" : "auto" }}>
                  {playingAnswer?.answerId === answer.id ? (
                    <video
                      controls autoPlay
                      style={{ width: isMobile ? "100%" : 280, borderRadius: 8, background: "#000" }}
                      src={getScreeningVideoUrl(s.id, answer.id)}
                    />
                  ) : (
                    <button
                      onClick={() => setPlayingAnswer({ sessionId: s.id, answerId: answer.id })}
                      style={{ ...outlineBtn, display: "flex", alignItems: "center", gap: 6 }}
                    >
                      ▶ Play Video
                    </button>
                  )}
                  {answer.duration_seconds && (
                    <div style={{ fontSize: 12, color: "#94a3b8", marginTop: 4, textAlign: "center" }}>
                      {Math.floor(answer.duration_seconds / 60)}:{String(answer.duration_seconds % 60).padStart(2, "0")}
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>
        ))}
      </div>
    );
  }

  // ── Main view ─────────────────────────────────────────────────────────────
  const STATUS_LABELS_MAP: Record<string, string> = {
    pending: "Pending", in_progress: "In Progress", completed: "Completed",
    processing: "Processing", scored: "Scored", expired: "Expired", error: "Score Error",
  };

  const allSessions = sessions as any[];
  const scoredList = allSessions.filter(s => s.status === "scored" && s.overall_score != null);
  const avgScore = scoredList.length ? (scoredList.reduce((sum, s) => sum + s.overall_score, 0) / scoredList.length) : null;

  return (
    <div>
      {/* Width and padding come from ShellMain (80px 32px 32px, and the
          mobile override in responsive.css). Capping the page here made it
          narrower than every sibling list page and squeezed the table until
          the actions column scrolled off. Matches CandidatesPage. */}
      <LoadingOverlay active={
        saveTmplMut.isPending || generateMut.isPending || inviteMut.isPending ||
        deleteMut.isPending || rescoreMut.isPending || resendMut.isPending
      } />

      {/* Header */}
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: 20, flexWrap: "wrap", gap: 12 }}>
        <div>
          <h2 style={{ margin: 0, fontSize: isMobile ? 18 : 22, fontWeight: 700, color: "#0f172a" }}>AI Video Screening</h2>
          {!isMobile && <div style={{ fontSize: 13, color: "#64748b", marginTop: 3 }}>Async video interviews scored by AI before human review</div>}
        </div>
        <button onClick={() => setShowInviteModal(true)}
          style={{ padding: "9px 18px", background: "linear-gradient(135deg, #1e3a8a, #2563eb)", color: "white", border: "none", borderRadius: 8, fontSize: 13, fontWeight: 600, cursor: "pointer", whiteSpace: "nowrap" }}>
          + Send Invite
        </button>
      </div>

      <div className="tab-pill-bar" style={{ marginBottom: 20 }}>
        {([["sessions", "Sessions"], ["rankings", "Rankings"], ["setup", "Setup"]] as const).map(([t, label]) => (
          <button key={t} onClick={() => setTab(t)} className={tab === t ? "active" : ""}>{label}</button>
        ))}
      </div>

      {tab === "sessions" && (
        <>
          {/* Stats strip */}
          {allSessions.length > 0 && (
            <div style={{ display: "flex", gap: 12, marginBottom: 20, flexWrap: "wrap" }}>
              {[
                { label: "Total Sent", value: allSessions.length, color: "#0f172a", bg: "#f8fafc", border: "#e2e8f0" },
                { label: "Scored", value: scoredList.length, color: "#166534", bg: "#f0fdf4", border: "#86efac" },
                { label: "Avg Score", value: avgScore != null ? `${avgScore.toFixed(1)}/5` : "—", color: "#0f766e", bg: "#f0fdf4", border: "#86efac" },
                { label: "Pending", value: allSessions.filter(s => ["pending", "in_progress"].includes(s.status)).length, color: "#1d4ed8", bg: "#eff6ff", border: "#bfdbfe" },
                { label: "Errors", value: allSessions.filter(s => s.status === "error").length, color: "#991b1b", bg: "#fff1f2", border: "#fecdd3", hide: allSessions.filter(s => s.status === "error").length === 0 },
              ].filter(st => !st.hide).map(st => (
                <div key={st.label} style={{ background: st.bg, border: `1px solid ${st.border}`, borderRadius: 10, padding: "12px 20px", flex: "1 1 100px", minWidth: 90 }}>
                  <div style={{ fontSize: 22, fontWeight: 800, color: st.color }}>{st.value}</div>
                  <div style={{ fontSize: 11, color: "#64748b", fontWeight: 500, marginTop: 1 }}>{st.label}</div>
                </div>
              ))}
            </div>
          )}

          {/* Filter chips + search */}
          <div style={{ display: "flex", gap: 8, marginBottom: 16, flexWrap: "wrap", alignItems: "center" }}>
            {(["all", "strong_yes", "yes", "maybe", "no"] as const).map(r => {
              const count = r === "all" ? allSessions.length : allSessions.filter((s: any) => s.recommendation === r).length;
              const rec = r !== "all" ? REC_COLORS[r] : null;
              const active = recFilter === r;
              return (
                <button key={r} onClick={() => setRecFilter(r)} style={{
                  padding: "5px 12px", borderRadius: 20, fontSize: 11, fontWeight: 600, cursor: "pointer", whiteSpace: "nowrap",
                  border: `2px solid ${active ? (rec?.color || "#1e3a8a") : "transparent"}`,
                  background: rec ? rec.bg : (active ? "#eff6ff" : "#f8fafc"),
                  color: active ? (rec?.color || "#1e3a8a") : "#64748b",
                }}>
                  {r === "all" ? "All" : REC_COLORS[r].label} ({count})
                </button>
              );
            })}
            {!isMobile && (
              <input value={sessionSearch} onChange={e => setSessionSearch(e.target.value)}
                placeholder="Search candidate or job…"
                style={{ marginLeft: "auto", padding: "6px 12px", border: "1px solid #e2e8f0", borderRadius: 8, fontSize: 13, width: 220, outline: "none", color: "#334155" }} />
            )}
            {allSessions.length > 0 && (
              <AsyncButton onClick={() => exportScreeningSessions().catch(() => {})} loadingText="Exporting…"
                style={{ padding: "6px 14px", border: "1px solid #e2e8f0", borderRadius: 8, fontSize: 12, fontWeight: 600, background: "#f8fafc", color: "#475569", whiteSpace: "nowrap" }}>
                ↓ Export CSV
              </AsyncButton>
            )}
          </div>

          {loadingSessions ? (
            <div style={{ textAlign: "center", padding: 60, color: "#64748b" }}>Loading…</div>
          ) : allSessions.length === 0 ? (
            <div style={{ background: "white", border: "1px solid #e2e8f0", borderRadius: 12, textAlign: "center", padding: "56px 24px", color: "#94a3b8" }}>
              <div style={{ fontSize: 36, marginBottom: 12 }}>🎥</div>
              <div style={{ fontWeight: 600, fontSize: 15, color: "#334155", marginBottom: 6 }}>No screening sessions yet</div>
              <div style={{ fontSize: 14, marginBottom: 20 }}>Send a candidate their video interview link to get started.</div>
              <button onClick={() => setShowInviteModal(true)}
                style={{ padding: "9px 20px", background: "linear-gradient(135deg, #1e3a8a, #2563eb)", color: "white", border: "none", borderRadius: 8, fontSize: 13, fontWeight: 600, cursor: "pointer" }}>
                + Send First Invite
              </button>
            </div>
          ) : isMobile ? (
            /* ── Mobile: card list ── */
            (() => {
              const filtered = allSessions.filter((s: any) => {
                if (recFilter !== "all" && s.recommendation !== recFilter) return false;
                if (sessionSearch) { const q = sessionSearch.toLowerCase(); return (s.candidate_name || "").toLowerCase().includes(q) || (s.job_title || "").toLowerCase().includes(q); }
                return true;
              });
              if (filtered.length === 0) return (
                <div style={{ background: "white", border: "1px solid #e2e8f0", borderRadius: 12, textAlign: "center", color: "#94a3b8", padding: 32 }}>No sessions match this filter.</div>
              );
              return (
                <div>
                  {filtered.map((s: any) => {
                    const sc = STATUS_COLORS[s.status] || STATUS_COLORS.pending;
                    const rec = s.recommendation ? REC_COLORS[s.recommendation] : null;
                    const stuckProcessing = s.status === "processing" &&
                      s.completed_at && (Date.now() - new Date(s.completed_at).getTime()) > 5 * 60 * 1000;
                    const showScoreNow = s.status === "completed" || s.status === "error" || s.status === "scored" || stuckProcessing;
                    return (
                      <div key={s.id} onClick={() => setSessionDetail(s)}
                        style={{ background: "white", border: "1px solid #e2e8f0", borderRadius: 10, padding: 16, marginBottom: 10, cursor: "pointer" }}>
                        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: 8, gap: 8 }}>
                          <div style={{ minWidth: 0, flex: 1 }}>
                            <div style={{ fontWeight: 700, fontSize: 14, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", color: "#0f172a" }}>{s.candidate_name || "—"}</div>
                            <div style={{ color: "#64748b", fontSize: 12, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{s.candidate_email}</div>
                            <div style={{ color: "#94a3b8", fontSize: 12, marginTop: 2 }}>{s.job_title || "—"}</div>
                          </div>
                          <div style={{ display: "flex", flexDirection: "column", alignItems: "flex-end", gap: 4, flexShrink: 0 }}>
                            <span style={{ background: sc.bg, color: sc.color, borderRadius: 5, padding: "2px 8px", fontSize: 11, fontWeight: 600 }}>
                              {STATUS_LABELS_MAP[s.status] || s.status}
                            </span>
                            {s.overall_score != null && (
                              <span style={{ fontWeight: 700, fontSize: 15, color: "#0f766e" }}>{s.overall_score.toFixed(1)}<span style={{ fontSize: 11, color: "#94a3b8" }}>/5</span></span>
                            )}
                            {rec && <span style={{ background: rec.bg, color: rec.color, borderRadius: 5, padding: "2px 8px", fontSize: 11, fontWeight: 600 }}>{rec.label}</span>}
                            {s.integrity_flags && s.integrity_flags.length > 0 && (
                              <span title={flagTooltip(s.integrity_flags)}
                                style={{ background: "#fef3c7", color: "#92400e", borderRadius: 5, padding: "2px 8px", fontSize: 11, fontWeight: 700, cursor: "help" }}>
                                ⚠ {s.integrity_flags.length} flag{s.integrity_flags.length !== 1 ? "s" : ""}
                              </span>
                            )}
                          </div>
                        </div>
                        <div style={{ display: "flex", gap: 6, alignItems: "center", flexWrap: "wrap" }} onClick={e => e.stopPropagation()}>
                          <button onClick={() => setSessionDetail(s)}
                            style={{ background: "#f1f5f9", color: "#475569", border: "none", padding: "5px 12px", borderRadius: 6, cursor: "pointer", fontSize: 12, fontWeight: 600 }}>
                            View
                          </button>
                          {["pending", "expired"].includes(s.status) && (
                            <button onClick={() => resendMut.mutate(s.id)} disabled={resendMut.variables === s.id && resendMut.isPending}
                              style={{ background: "#f1f5f9", color: "#16a34a", border: "none", padding: "5px 12px", borderRadius: 6, cursor: "pointer", fontSize: 12, fontWeight: 600 }}>
                              {resendMut.variables === s.id && resendMut.isPending ? "…" : "Resend"}
                            </button>
                          )}
                          {showScoreNow && (
                            <button onClick={() => rescoreMut.mutate(s.id)} disabled={rescoreMut.variables === s.id && rescoreMut.isPending}
                              style={{ background: "#ede9fe", color: "#6d28d9", border: "none", padding: "5px 12px", borderRadius: 6, cursor: "pointer", fontSize: 12, fontWeight: 600 }}>
                              {rescoreMut.variables === s.id && rescoreMut.isPending ? "…" : "Score"}
                            </button>
                          )}
                          <button onClick={() => deleteMut.mutate(s.id)} disabled={deleteMut.isPending}
                            style={{ background: "#f1f5f9", color: "#dc2626", border: "none", padding: "5px 12px", borderRadius: 6, cursor: "pointer", fontSize: 12, fontWeight: 600 }}>
                            Delete
                          </button>
                        </div>
                      </div>
                    );
                  })}
                </div>
              );
            })()
          ) : (
            /* ── Desktop: table ── */
            <div style={{ background: "white", borderRadius: 12, border: "1px solid #e2e8f0", overflowX: "auto" }}>
              <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 13, minWidth: 780 }}>
                <thead>
                  <tr style={{ background: "#f8fafc", borderBottom: "1px solid #e2e8f0" }}>
                    {["Candidate", "Job", "Status", "Score", "Recommendation", "Sent / Expires", ""].map((h, i) => (
                      <th key={i} style={{ padding: "10px 14px", textAlign: "left", fontWeight: 600, color: "#475569", fontSize: 12, whiteSpace: "nowrap" }}>{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {(() => {
                    const filtered = allSessions.filter((s: any) => {
                      if (recFilter !== "all" && s.recommendation !== recFilter) return false;
                      if (sessionSearch) { const q = sessionSearch.toLowerCase(); return (s.candidate_name || "").toLowerCase().includes(q) || (s.job_title || "").toLowerCase().includes(q); }
                      return true;
                    });
                    if (filtered.length === 0) return (
                      <tr><td colSpan={7} style={{ padding: "48px 14px", textAlign: "center", color: "#94a3b8", fontSize: 14 }}>
                        No sessions match this filter.
                      </td></tr>
                    );
                    return filtered.map((s: any, i: number) => {
                      const sc = STATUS_COLORS[s.status] || STATUS_COLORS.pending;
                      const rec = s.recommendation ? REC_COLORS[s.recommendation] : null;
                      const stuckProcessing = s.status === "processing" &&
                        s.completed_at && (Date.now() - new Date(s.completed_at).getTime()) > 5 * 60 * 1000;
                      const showScoreNow = s.status === "completed" || s.status === "error" || s.status === "scored" || stuckProcessing;
                      const expiresAt = s.expires_at ? new Date(s.expires_at) : null;
                      const daysLeft = expiresAt ? Math.ceil((expiresAt.getTime() - Date.now()) / 86400000) : null;
                      const expiryLabel = daysLeft !== null
                        ? (daysLeft <= 0 ? "Expired" : daysLeft === 1 ? "Expires tomorrow" : `Expires in ${daysLeft}d`)
                        : null;
                      const expiryColor = daysLeft !== null && daysLeft <= 2 ? "#dc2626" : "#94a3b8";
                      return (
                        <tr key={s.id}
                          style={{ borderBottom: i < filtered.length - 1 ? "1px solid #f1f5f9" : "none", background: i % 2 === 0 ? "white" : "#fafafa", cursor: "pointer", transition: "background 0.1s" }}
                          onMouseEnter={e => (e.currentTarget.style.background = "#f0f9ff")}
                          onMouseLeave={e => (e.currentTarget.style.background = i % 2 === 0 ? "white" : "#fafafa")}
                          onClick={() => setSessionDetail(s)}>
                          <td style={{ padding: "11px 14px" }}>
                            <div style={{ fontWeight: 600, color: "#0f172a" }}>{s.candidate_name || "—"}</div>
                            <div style={{ color: "#94a3b8", fontSize: 11 }}>{s.candidate_email}</div>
                          </td>
                          <td style={{ padding: "11px 14px", color: "#334155", maxWidth: 180, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{s.job_title || "—"}</td>
                          <td style={{ padding: "11px 14px" }}>
                            <span style={{ background: sc.bg, color: sc.color, borderRadius: 5, padding: "3px 8px", fontSize: 11, fontWeight: 600, whiteSpace: "nowrap" }}>
                              {STATUS_LABELS_MAP[s.status] || s.status}
                            </span>
                          </td>
                          <td style={{ padding: "11px 14px" }}>
                            {s.overall_score != null ? (
                              <div>
                                <span style={{ fontWeight: 700, fontSize: 15, color: s.overall_score >= 4 ? "#16a34a" : s.overall_score >= 3 ? "#d97706" : "#dc2626" }}>
                                  {s.overall_score.toFixed(1)}
                                </span>
                                <span style={{ fontSize: 11, color: "#94a3b8" }}>/5</span>
                              </div>
                            ) : <span style={{ color: "#94a3b8" }}>—</span>}
                          </td>
                          <td style={{ padding: "11px 14px" }}>
                            {rec ? <span style={{ background: rec.bg, color: rec.color, borderRadius: 5, padding: "3px 8px", fontSize: 11, fontWeight: 600, whiteSpace: "nowrap" }}>{rec.label}</span> : <span style={{ color: "#94a3b8" }}>—</span>}
                          </td>
                          <td style={{ padding: "11px 14px", fontSize: 12, whiteSpace: "nowrap" }}>
                            <div style={{ color: "#475569" }}>{formatDate(s.sent_at, "short", tz)}</div>
                            {["pending", "in_progress", "expired"].includes(s.status) && expiryLabel && (
                              <div style={{ color: expiryColor, fontWeight: 600, fontSize: 11, marginTop: 2 }}>{expiryLabel}</div>
                            )}
                          </td>
                          <td style={{ padding: "11px 14px" }} onClick={e => e.stopPropagation()}>
                            <div style={{ display: "flex", gap: 4, alignItems: "center" }}>
                              {s.resume_count > 0 && (
                                <span title={`Resumed ${s.resume_count}× after browser crash`}
                                  style={{ background: "#fef3c7", color: "#92400e", borderRadius: 5, padding: "2px 6px", fontSize: 11, fontWeight: 700 }}>
                                  ↩{s.resume_count}
                                </span>
                              )}
                              {s.integrity_flags && s.integrity_flags.length > 0 && (
                                <span title={flagTooltip(s.integrity_flags)}
                                  style={{ background: "#fef3c7", color: "#92400e", borderRadius: 5, padding: "2px 6px", fontSize: 11, fontWeight: 700, cursor: "help" }}>
                                  ⚠ {s.integrity_flags.length}
                                </span>
                              )}
                              <button onClick={() => setSessionDetail(s)}
                                style={{ background: "#f1f5f9", color: "#475569", border: "none", padding: "4px 10px", borderRadius: 6, cursor: "pointer", fontSize: 12, fontWeight: 600, whiteSpace: "nowrap" }}>
                                View
                              </button>
                              {["pending", "expired"].includes(s.status) && (
                                <button onClick={() => resendMut.mutate(s.id)} disabled={resendMut.variables === s.id && resendMut.isPending}
                                  style={{ background: "#f1f5f9", color: "#16a34a", border: "none", padding: "4px 10px", borderRadius: 6, cursor: "pointer", fontSize: 12, fontWeight: 600, whiteSpace: "nowrap" }}>
                                  {resendMut.variables === s.id && resendMut.isPending ? "…" : "Resend"}
                                </button>
                              )}
                              {showScoreNow && (
                                <button onClick={() => rescoreMut.mutate(s.id)} disabled={rescoreMut.variables === s.id && rescoreMut.isPending}
                                  style={{ background: "#f1f5f9", color: "#6d28d9", border: "none", padding: "4px 10px", borderRadius: 6, cursor: "pointer", fontSize: 12, fontWeight: 600, whiteSpace: "nowrap" }}>
                                  {rescoreMut.variables === s.id && rescoreMut.isPending ? "…" : "Score"}
                                </button>
                              )}
                              <button onClick={() => deleteMut.mutate(s.id)} disabled={deleteMut.isPending}
                                style={{ background: "#f1f5f9", color: "#dc2626", border: "none", padding: "4px 10px", borderRadius: 6, cursor: "pointer", fontSize: 12, fontWeight: 600, whiteSpace: "nowrap" }}>
                                Delete
                              </button>
                            </div>
                          </td>
                        </tr>
                      );
                    });
                  })()}
                </tbody>
              </table>
            </div>
          )}
        </>
      )}

      {tab === "rankings" && (() => {
        const scoredSessions = (sessions as any[]).filter((s: any) => s.status === "scored" && s.overall_score != null);
        if (scoredSessions.length === 0) {
          return (
            <div style={{ ...card, textAlign: "center", padding: 48, color: "#94a3b8" }}>
              No scored sessions yet. Rankings appear once AI scoring completes.
            </div>
          );
        }
        const byJob: Record<string, any[]> = {};
        scoredSessions.forEach((s: any) => {
          const key = s.job_title || `Job #${s.job_id}`;
          if (!byJob[key]) byJob[key] = [];
          byJob[key].push(s);
        });
        Object.values(byJob).forEach(arr => arr.sort((a, b) => b.overall_score - a.overall_score));
        // Only show jobs with 2+ candidates — ranking 1 person is meaningless
        const rankableJobs = Object.entries(byJob).filter(([, candidates]) => candidates.length >= 2);
        if (rankableJobs.length === 0) {
          return (
            <div style={{ ...card, textAlign: "center", padding: 48 }}>
              <div style={{ fontSize: 32, marginBottom: 12 }}>📊</div>
              <div style={{ fontWeight: 600, fontSize: 15, color: "#334155", marginBottom: 8 }}>No rankings yet</div>
              <div style={{ fontSize: 14, color: "#94a3b8", maxWidth: 340, margin: "0 auto", lineHeight: 1.6 }}>
                Rankings appear once you've screened 2 or more candidates for the same role. Send more invites to start comparing.
              </div>
            </div>
          );
        }
        return (
          <div>
            {rankableJobs.map(([jobTitle, candidates]) => (
              <div key={jobTitle} style={{ ...card, padding: 0, overflow: "hidden", marginBottom: 20 }}>
                <div style={{ padding: "14px 18px", background: "#f8fafc", borderBottom: "1px solid #e2e8f0", display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                  <div style={{ fontWeight: 700, fontSize: 15 }}>{jobTitle}</div>
                  <div style={{ fontSize: 13, color: "#64748b" }}>{candidates.length} candidate{candidates.length !== 1 ? "s" : ""}</div>
                </div>
                <div>
                  {candidates.map((s: any, rank: number) => {
                    const rec = s.recommendation ? REC_COLORS[s.recommendation] : null;
                    const rankColor = rank === 0 ? "#d97706" : rank === 1 ? "#94a3b8" : rank === 2 ? "#b45309" : "#cbd5e1";
                    const pct = (s.overall_score / 5) * 100;
                    const barColor = s.overall_score >= 4 ? "#16a34a" : s.overall_score >= 3 ? "#d97706" : "#dc2626";
                    return (
                      <div key={s.id}
                        onClick={() => setSessionDetail(s)}
                        style={{ padding: isMobile ? "12px 14px" : "14px 18px", borderBottom: "1px solid #f1f5f9", cursor: "pointer", display: "flex", alignItems: "center", gap: 12 }}
                        onMouseEnter={e => (e.currentTarget.style.background = "#f8fafc")}
                        onMouseLeave={e => (e.currentTarget.style.background = "")}>
                        <div style={{ width: 30, height: 30, borderRadius: "50%", background: rankColor, color: "#fff", display: "flex", alignItems: "center", justifyContent: "center", fontWeight: 800, fontSize: 13, flexShrink: 0 }}>
                          {rank + 1}
                        </div>
                        <div style={{ flex: 1, minWidth: 0 }}>
                          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
                            <div style={{ minWidth: 0 }}>
                              <span style={{ fontWeight: 600, fontSize: 14 }}>{s.candidate_name || "—"}</span>
                              {!isMobile && <span style={{ color: "#94a3b8", fontSize: 12, marginLeft: 8 }}>{s.candidate_email}</span>}
                            </div>
                            <div style={{ display: "flex", gap: 8, alignItems: "center", flexShrink: 0 }}>
                              {rec && <span style={{ background: rec.bg, color: rec.color, borderRadius: 5, padding: "2px 8px", fontSize: 12, fontWeight: 700 }}>{rec.label}</span>}
                              <span style={{ fontWeight: 800, fontSize: 18, color: barColor }}>{s.overall_score.toFixed(1)}<span style={{ fontSize: 12, color: "#94a3b8", fontWeight: 400 }}>/5</span></span>
                            </div>
                          </div>
                          <div style={{ marginTop: 6, height: 5, background: "#e2e8f0", borderRadius: 3 }}>
                            <div style={{ width: `${pct}%`, height: "100%", background: barColor, borderRadius: 3 }} />
                          </div>
                          {!isMobile && s.ai_summary && (
                            <div style={{ fontSize: 12, color: "#64748b", marginTop: 5, lineHeight: 1.5, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                              {s.ai_summary}
                            </div>
                          )}
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            ))}
          </div>
        );
      })()}

      {tab === "setup" && (
        <div style={{ display: "flex", gap: 20, flexWrap: "wrap" }}>
          {/* Job selector — collapses to selected chip on mobile once a job is picked */}
          <div style={{ width: isMobile ? "100%" : 220, flexShrink: 0 }}>
            {isMobile && selectedJobId ? (
              /* Collapsed: show selected job + Change button */
              <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 4 }}>
                <div style={{ flex: 1, padding: "10px 14px", borderRadius: 8, background: "#f0fdf4", border: "1px solid #86efac", fontWeight: 600, fontSize: 14, color: "#1e293b", wordBreak: "break-word" }}>
                  {activeJobs.find((j: any) => j.id === selectedJobId)?.title || "Selected"}
                </div>
                <button onClick={() => setSelectedJobId(null)} style={{ flexShrink: 0, padding: "8px 12px", borderRadius: 8, border: "1px solid #e2e8f0", background: "#fff", fontSize: 13, color: "#475569", cursor: "pointer", fontWeight: 500 }}>
                  Change
                </button>
              </div>
            ) : (
              <>
                <div style={{ fontSize: 12, fontWeight: 700, color: "#64748b", marginBottom: 8 }}>SELECT JOB</div>
                <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
                  {activeJobs.map((j: any) => (
                    <div key={j.id}
                      onClick={() => setSelectedJobId(j.id)}
                      style={{
                        padding: "10px 14px", borderRadius: 8, cursor: "pointer",
                        background: selectedJobId === j.id ? "#f0fdf4" : "#fff",
                        border: `1px solid ${selectedJobId === j.id ? "#86efac" : "#e2e8f0"}`,
                        fontWeight: selectedJobId === j.id ? 600 : 400, fontSize: 14, color: "#1e293b",
                        wordBreak: "break-word",
                      }}>
                      {j.title}
                    </div>
                  ))}
                  {activeJobs.length === 0 && <div style={{ fontSize: 13, color: "#94a3b8" }}>No open jobs</div>}
                </div>
              </>
            )}
          </div>

          <div style={{ flex: 1, minWidth: 0 }}>
            {!selectedJobId ? (
              <div style={{ ...card, textAlign: "center", padding: 48, color: "#94a3b8" }}>
                Select a job to configure its screening template
              </div>
            ) : loadingTemplate ? (
              <div style={{ ...card, textAlign: "center", padding: 40, color: "#94a3b8" }}>Loading…</div>
            ) : tmplForm ? (
              <div style={card}>
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 20, flexWrap: "wrap", gap: 10 }}>
                  <div style={{ fontWeight: 700, fontSize: 16 }}>Screening Template</div>
                  <div style={{ display: "flex", alignItems: "center", gap: 14, flexWrap: "wrap" }}>
                    <button
                      onClick={() => generateMut.mutate()}
                      disabled={generateMut.isPending}
                      style={{ ...outlineBtn, fontSize: 13, padding: "6px 14px", display: "flex", alignItems: "center", gap: 6 }}>
                      {generateMut.isPending ? "Generating…" : "✨ Generate with AI"}
                    </button>
                    <label style={{ display: "flex", alignItems: "center", gap: 8, cursor: "pointer" }}>
                      <input type="checkbox" checked={tmplForm.enabled}
                        onChange={e => setTmplForm((f: any) => ({ ...f, enabled: e.target.checked }))} />
                      <span style={{ fontSize: 14, fontWeight: 600 }}>Enabled</span>
                    </label>
                  </div>
                </div>

                <div style={{ marginBottom: 16 }}>
                  <label style={labelStyle}>Intro Text (shown to candidate before they start)</label>
                  <textarea value={tmplForm.intro_text}
                    onChange={e => setTmplForm((f: any) => ({ ...f, intro_text: e.target.value }))}
                    rows={3} style={{ ...inputStyle, resize: "vertical" }}
                    placeholder="e.g. Thank you for your interest! Please answer the following questions on video." />
                </div>

                <div style={{ marginBottom: 16 }}>
                  <label style={labelStyle}>Practice / Warm-up Question (unscored, shown before real questions)</label>
                  <textarea value={tmplForm.practice_question}
                    onChange={e => setTmplForm((f: any) => ({ ...f, practice_question: e.target.value }))}
                    rows={2} style={{ ...inputStyle, resize: "vertical" }}
                    placeholder="e.g. Tell us about yourself and why you're interested in this role." />
                </div>

                <div style={{ marginBottom: 16 }}>
                  <label style={labelStyle}>Default Answer Time Limit (seconds)</label>
                  <input type="number" value={tmplForm.time_limit_seconds} min={30} max={600}
                    onChange={e => setTmplForm((f: any) => ({ ...f, time_limit_seconds: parseInt(e.target.value) || 120 }))}
                    style={{ ...inputStyle, maxWidth: 200 }} />
                </div>

                <label style={{ display: "flex", alignItems: "center", gap: 10, cursor: "pointer", marginBottom: 20, padding: "12px 14px", background: "#f8fafc", border: "1px solid #e2e8f0", borderRadius: 8 }}>
                  <input type="checkbox" checked={tmplForm.notify_hr}
                    onChange={e => setTmplForm((f: any) => ({ ...f, notify_hr: e.target.checked }))} />
                  <div>
                    <div style={{ fontWeight: 600, fontSize: 14 }}>Notify HR on invite sent &amp; scoring complete</div>
                    <div style={{ fontSize: 12, color: "#64748b", marginTop: 2 }}>Recruiters and HR-role users receive an email when the invite is sent, and again when AI scoring finishes.</div>
                  </div>
                </label>

                <div style={{ marginBottom: 12 }}>
                  <div style={{ fontWeight: 700, fontSize: 14 }}>Questions</div>
                </div>
                {tmplForm.questions.map((q: any, i: number) => (
                  <div key={i} style={{ border: "1px solid #e2e8f0", borderRadius: 8, padding: 14, marginBottom: 12, background: "#fafafa" }}>
                    <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 8 }}>
                      <span style={{ fontWeight: 600, fontSize: 13, color: "#64748b" }}>Question {i + 1}</span>
                      <button onClick={() => removeQuestion(i)} style={dangerBtn}>Remove</button>
                    </div>
                    <textarea value={q.question_text}
                      onChange={e => updateQuestion(i, "question_text", e.target.value)}
                      rows={2} placeholder="e.g. Tell me about your experience with..." style={{ ...inputStyle, marginBottom: 8, resize: "vertical" }} />
                    <textarea value={q.rubric || ""}
                      onChange={e => updateQuestion(i, "rubric", e.target.value)}
                      rows={2} placeholder="Grading rubric (optional): 5=specific examples, 3=general answer, 1=vague"
                      style={{ ...inputStyle, resize: "vertical", fontSize: 13 }} />
                    <div style={{ marginTop: 8 }}>
                      <label style={{ fontSize: 12, color: "#64748b" }}>Custom time limit (seconds, leave blank to use default)</label>
                      <input type="number" min={30} max={600}
                        value={q.time_limit_seconds || ""}
                        onChange={e => updateQuestion(i, "time_limit_seconds", e.target.value ? parseInt(e.target.value) : null)}
                        style={{ ...inputStyle, maxWidth: 140, marginTop: 4 }} />
                    </div>
                  </div>
                ))}
                <div style={{ display: "flex", gap: 10, marginTop: 4, flexWrap: "wrap" }}>
                  <button onClick={addQuestion} style={outlineBtn}>+ Add Question</button>
                  {(screeningDefaults?.question_bank?.length ?? 0) > 0 && (
                    <select
                      value=""
                      onChange={e => {
                        const q = screeningDefaults!.question_bank[parseInt(e.target.value)];
                        if (q) setTmplForm((f: any) => ({ ...f, questions: [...f.questions, { question_text: q.question, rubric: "", time_limit_seconds: null }] }));
                      }}
                      style={{ ...inputStyle, maxWidth: 260, marginBottom: 0 }}
                    >
                      <option value="">+ Add from question bank…</option>
                      {screeningDefaults!.question_bank.map((q, i) => (
                        <option key={i} value={i}>
                          {(q.role_family ? `[${q.role_family}] ` : "") + q.question.slice(0, 60)}
                        </option>
                      ))}
                    </select>
                  )}
                  <button
                    onClick={() => saveTmplMut.mutate(tmplForm)}
                    disabled={saveTmplMut.isPending || tmplForm.questions.every((q: any) => !q.question_text.trim())}
                    style={{ ...btnStyle, opacity: saveTmplMut.isPending ? 0.6 : 1 }}>
                    {saveTmplMut.isPending ? "Saving…" : "Save Template"}
                  </button>
                </div>
              </div>
            ) : null}
          </div>
        </div>
      )}

      {/* Invite Modal */}
      {showInviteModal && (
        <div style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.5)", display: "flex", alignItems: "center", justifyContent: "center", zIndex: 1000, padding: 16 }}>
          <div style={{ background: "#fff", borderRadius: 12, padding: isMobile ? 20 : 28, width: "100%", maxWidth: 440 }}>
            <div style={{ fontWeight: 700, fontSize: 18, marginBottom: 20 }}>Send Screening Invite</div>
            <div style={{ marginBottom: 14 }}>
              <label style={labelStyle}>Job</label>
              <select value={inviteJobId || ""} onChange={e => setInviteJobId(parseInt(e.target.value))} style={inputStyle}>
                <option value="">Select job…</option>
                {(configuredTemplates as any[]).map((t: any) => <option key={t.job_id} value={t.job_id}>{t.job_title}</option>)}
              </select>
              {(configuredTemplates as any[]).length === 0 && (
                <div style={{ fontSize: 12, color: "#d97706", marginTop: 6 }}>
                  No jobs have a screening template yet. Go to <strong>Setup</strong> tab to create one first.
                </div>
              )}
            </div>
            <div style={{ marginBottom: 14 }}>
              <label style={labelStyle}>Candidate Name</label>
              <input value={inviteName} onChange={e => setInviteName(e.target.value)} style={inputStyle} placeholder="e.g. John Smith" />
            </div>
            <div style={{ marginBottom: 20 }}>
              <label style={labelStyle}>Candidate Email</label>
              <input type="email" value={inviteEmail} onChange={e => setInviteEmail(e.target.value)} style={inputStyle} placeholder="candidate@email.com" />
            </div>
            <div style={{ display: "flex", gap: 10, justifyContent: "flex-end" }}>
              <button onClick={() => setShowInviteModal(false)} style={outlineBtn}>Cancel</button>
              <button
                onClick={() => {
                  // bug-83: name was optional here, so invites routinely went out with a
                  // permanently blank candidate_name even though nothing downstream
                  // (session list, email template) has any other way to show who it's for.
                  if (!inviteName.trim()) {
                    toast("Please enter the candidate's name", "error");
                    return;
                  }
                  if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(inviteEmail)) {
                    toast("Please enter a valid email address", "error");
                    return;
                  }
                  inviteMut.mutate({ job_id: inviteJobId, candidate_email: inviteEmail, candidate_name: inviteName });
                }}
                disabled={!inviteJobId || !inviteEmail || !inviteName.trim() || inviteMut.isPending}
                style={{ ...btnStyle, opacity: !inviteJobId || !inviteEmail || !inviteName.trim() ? 0.5 : 1 }}>
                {inviteMut.isPending ? "Sending…" : "Send Invite"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
